import {
  CSSProperties,
  FC,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from "react";

import styled from "@emotion/styled";
import { Modal } from "@9amhealth/shared";
import translate from "src/lib/translate";
import Translate from "src/ui/components/Translate/Translate";

import IconMinimise from "./IconMinimise";

// Fill the screen with a 16px margin from the top (below the safe area) and
// 16px above the bottom nav bar. `alert` centers its card with fixed padding by
// default, so we override the overlay's alignment/padding inline to stretch the
// card into those exact insets instead.
const MODAL_STYLE = {
  "--dialog-background": "var(--color-white, #fff)",
  "--border-radius": "1.5rem",
  "--width": "100%",
  "--max-width": "700px",
  "--max-height": "100%",
  alignItems: "stretch",
  padding:
    "calc(var(--safe-area-inset-top, 0px) + 16px) 16px calc(var(--safe-area-inset-bottom, 0px) + var(--navbar-padding-bottom, 55px) + 16px)"
} as CSSProperties;

const Panel = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  padding: 1.25rem 0 0;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0 1.25rem 0.75rem;
`;

const Label = styled.p`
  flex: 1 0 0;
  margin: 0;
  color: var(--color-charcoal-60);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.33;
  letter-spacing: 0.48px;
  text-transform: uppercase;
`;

const MinimiseButton = styled.button`
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--Greys-Gray, #aaa7a0);
  line-height: 0;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const ScrollWrap = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
`;

const ScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 1.5rem calc(2rem + env(safe-area-inset-bottom, 0px));
`;

const Insight = styled.p`
  margin: 0;
  color: var(--color-charcoal, #212121);
  font-family: "STK Bureau Serif", serif;
  font-size: 1.125rem;
  line-height: 1.5;
  letter-spacing: -0.18px;
  white-space: pre-line;
`;

const Footer = styled.p`
  margin: 1rem 0 0;
  color: var(--color-charcoal-60);
  font-size: 0.75rem;
  line-height: 1.33;
`;

const Fog = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 3.5rem;
  pointer-events: none;

  &[data-edge="top"] {
    top: 0;
    background: linear-gradient(
      to top,
      rgba(255, 255, 255, 0),
      var(--color-white, #fff)
    );
  }

  &[data-edge="bottom"] {
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0),
      var(--color-white, #fff)
    );
  }
`;

interface MealTodayInsightModalProps {
  insights: string;
  footer?: string;
  onClose: () => void;
}

const MealTodayInsightModal: FC<MealTodayInsightModalProps> = ({
  insights,
  footer,
  onClose
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [showTopFog, setShowTopFog] = useState(false);
  const [showBottomFog, setShowBottomFog] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const measureFog = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const overflowing = el.scrollHeight - el.clientHeight > 1;
    setShowTopFog(overflowing && el.scrollTop > 1);
    setShowBottomFog(
      overflowing && el.scrollTop + el.clientHeight < el.scrollHeight - 1
    );
  }, []);

  useLayoutEffect(() => {
    measureFog();
  }, [measureFog, insights]);

  return (
    <Modal
      isOpen={isOpen}
      variant="alert"
      style={MODAL_STYLE}
      ariaLabel={translate("mealInsights.today.insightsLabel")}
      onWillDismiss={() => setIsOpen(false)}
      onDidDismiss={onClose}
    >
      <Panel>
        <Header>
          <Label>
            <Translate msg="mealInsights.today.insightsLabel" />
          </Label>
          <MinimiseButton
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label={translate("mealInsights.today.collapse")}
          >
            <IconMinimise />
          </MinimiseButton>
        </Header>
        <ScrollWrap>
          <ScrollArea ref={scrollRef} onScroll={measureFog}>
            <Insight>{insights}</Insight>
            {footer && <Footer>{footer}</Footer>}
          </ScrollArea>
          {showTopFog && <Fog data-edge="top" aria-hidden />}
          {showBottomFog && <Fog data-edge="bottom" aria-hidden />}
        </ScrollWrap>
      </Panel>
    </Modal>
  );
};

export default MealTodayInsightModal;
